import React, { useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts/core';
import { US_STATE_COORDS, STATE_ABBR_TO_NAME } from '../../data/usStates';

interface StateData {
    ship_state: string;
    order_count: number;
    sales: number;
    units: number;
}

interface USMapChartProps {
    data: StateData[];
    country?: string;
}

const USMapChart: React.FC<USMapChartProps> = ({ data, country = 'US' }) => {
    const [mapLoaded, setMapLoaded] = useState(false);
    const [loadError, setLoadError] = useState(false);

    useEffect(() => {
        // Fetch real US States GeoJSON
        fetch('https://raw.githubusercontent.com/PublicaMundi/MappingAPI/master/data/geojson/us-states.json')
            .then(response => {
                if (!response.ok) throw new Error('Network response was not ok');
                return response.json();
            })
            .then(geojson => {
                // Filter out Alaska and Hawaii to focus on continental US
                const filteredFeatures = geojson.features.filter((feature: any) => {
                    const stateName = feature.properties?.name;
                    return stateName !== 'Alaska' && stateName !== 'Hawaii';
                });
                const filteredGeoJson = { ...geojson, features: filteredFeatures };
                echarts.registerMap('USA', filteredGeoJson);
                setMapLoaded(true);
            })
            .catch(err => {
                console.error('Failed to load US map JSON:', err);
                setLoadError(true);
            });
    }, []);

    if (!data || data.length === 0) {
        return (
            <div style={{
                height: 500,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#9ca3af',
                background: '#f9fafb',
                borderRadius: 12,
                fontSize: 14,
                border: '1px solid #f3f4f6'
            }}>
                暂无地理分布数据，请选择日期范围后点击"分析"
            </div>
        );
    }

    if (loadError) {
        return (
            <div style={{ height: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444' }}>
                地图加载失败，请检查网络连接
            </div>
        );
    }

    if (!mapLoaded) {
        return (
            <div style={{ height: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280' }}>
                Loading Map...
            </div>
        );
    }

    const maxOrders = Math.max(...data.map(d => d.order_count));

    const scatterData = data
        .filter(d => US_STATE_COORDS[d.ship_state])
        .map(d => {
            const coords = US_STATE_COORDS[d.ship_state];
            const stateName = STATE_ABBR_TO_NAME[d.ship_state] || d.ship_state;
            return {
                name: stateName,
                value: [...coords, d.order_count],
                state: d.ship_state,
                order_count: d.order_count,
                sales: d.sales,
                units: d.units
            };
        });

    const displayCountry = country.toUpperCase() === 'US' ? '美国' : country;

    const option = {
        backgroundColor: 'transparent',
        title: {
            text: `${displayCountry} 订单地理分布`,
            left: 'center',
            top: 15,
            textStyle: {
                fontSize: 18,
                fontWeight: 600,
                color: '#111827'
            }
        },
        tooltip: {
            trigger: 'item',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderColor: '#e5e7eb',
            borderWidth: 1,
            padding: 12,
            textStyle: { color: '#374151', fontSize: 13 },
            formatter: (params: any) => {
                const d = params.data;
                if (!d || !d.state) return params.name; // Simple name for map background hover
                return `<div style="font-weight:600;font-size:14px;margin-bottom:8px">${d.name} <span style="color:#6b7280">(${d.state})</span></div>
                    <div style="display:flex;justify-content:space-between;margin:4px 0;gap:12px"><span>📦 订单数</span><b>${d.order_count?.toLocaleString() || 0}</b></div>
                    <div style="display:flex;justify-content:space-between;margin:4px 0;gap:12px"><span>💰 销售额</span><b>$${parseFloat(d.sales || 0).toLocaleString()}</b></div>
                    <div style="display:flex;justify-content:space-between;margin:4px 0;gap:12px"><span>📊 销量</span><b>${(d.units || 0).toLocaleString()}</b></div>`;
            }
        },
        geo: {
            map: 'USA',
            roam: true,
            zoom: 1.0,
            center: [-98, 38], // Center on continental US
            // Maximize map area
            top: 60,
            bottom: 20,
            left: 10,
            right: 10,
            label: {
                show: false
            },
            itemStyle: {
                areaColor: '#e0e7ff', // Lighter blue-gray
                borderColor: '#ffffff',
                borderWidth: 1.5,
                shadowColor: 'rgba(0, 0, 0, 0.1)',
                shadowBlur: 5
            },
            emphasis: {
                itemStyle: {
                    areaColor: '#c7d2fe'
                }
            }
        },
        visualMap: {
            min: 0,
            max: maxOrders,
            calculable: true,
            orient: 'vertical',
            right: 20,
            bottom: 30,
            inRange: {
                color: ['#60a5fa', '#3b82f6', '#2563eb', '#1d4ed8']
            },
            text: ['高', '低'],
            textStyle: { color: '#6b7280' },
            padding: 10,
            backgroundColor: 'rgba(255,255,255,0.8)',
            borderRadius: 4
        },
        series: [
            {
                name: '订单分布',
                type: 'scatter',
                coordinateSystem: 'geo',
                data: scatterData,
                symbolSize: (val: number[]) => {
                    return Math.max(6, Math.min(45, (val[2] / maxOrders) * 55));
                },
                encode: { value: 2 },
                itemStyle: {
                    color: '#3b82f6',
                    shadowBlur: 10,
                    shadowColor: 'rgba(59, 130, 246, 0.4)',
                    opacity: 0.9,
                    borderWidth: 1,
                    borderColor: '#fff'
                }
            },
            {
                name: 'Top 5',
                type: 'effectScatter',
                coordinateSystem: 'geo',
                data: scatterData.slice(0, 5),
                symbolSize: (val: number[]) => {
                    return Math.max(15, Math.min(55, (val[2] / maxOrders) * 65 + 10));
                },
                showEffectOn: 'render',
                rippleEffect: {
                    brushType: 'stroke',
                    scale: 2.5,
                    period: 4,
                    color: '#ef4444'
                },
                label: {
                    formatter: (p: any) => p.data.state,
                    position: 'inside',
                    show: true,
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: 11
                },
                itemStyle: {
                    color: '#ef4444',
                    shadowBlur: 10,
                    shadowColor: 'rgba(239, 68, 68, 0.5)',
                    borderWidth: 2,
                    borderColor: '#fff'
                },
                zlevel: 1
            }
        ]
    };

    return (
        <div style={{
            background: '#ffffff',
            borderRadius: 16,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            padding: '12px',
            border: '1px solid #e5e7eb'
        }}>
            <ReactECharts
                option={option}
                style={{ height: 550, width: '100%' }}
                opts={{ renderer: 'canvas' }}
            />
        </div>
    );
};

export default USMapChart;
